// Domain Setup Wizard Stimulus controller
// Handles multi-step modal wizard for custom domain configuration
// Also handles polling for domain status updates

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'modal',
    'modalTitle',
    'step',
    'stepIndicator',
    'form',
    'domainInput',
    'domainFeedback',
    'methodOption',
    'methodRadio',
    'confirmDomain',
    'confirmMethod',
    'backButton',
    'nextButton',
    'submitButton',
    // Real-time status targets
    'domainSection',
    'statusContainer',
    'statusMessage',
    'verificationAttempts'
  ]

  static values = {
    websiteId: String,
    status: String
  }

  connect() {
    this.currentStep = 1
    this.totalSteps = 3
    this.boundKeydown = this.handleKeydown.bind(this)
    this.pollInterval = null

    // Start polling if in a transitional state
    if (this.shouldPoll()) {
      this.startPolling()
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundKeydown)
    document.body.classList.remove('overflow-hidden')
    this.stopPolling()
  }

  // =====================
  // Status Polling
  // =====================

  shouldPoll() {
    // Poll when DNS config submitted, verifying, or waiting for SSL
    return ['pending', 'verifying', 'ssl_pending', 'verified'].includes(this.statusValue)
  }

  startPolling() {
    if (this.pollInterval) return

    // Poll every 10 seconds
    this.pollInterval = setInterval(() => {
      this.checkStatus()
    }, 10000)

    // Also check immediately
    setTimeout(() => this.checkStatus(), 2000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async checkStatus() {
    try {
      const response = await fetch(`/dashboard/website/${this.websiteIdValue}/domain_status`, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) return

      const html = await response.text()

      // Check if status changed by looking at response
      const statusChanged = this.detectStatusChange(html)

      if (statusChanged) {
        // Update the domain section with new content
        if (this.hasDomainSectionTarget) {
          this.domainSectionTarget.innerHTML = html
        }

        // Check if we should stop polling (connected or failed)
        if (html.includes('Custom Domain Active') || html.includes('Domain Setup Failed')) {
          this.stopPolling()

          // If connected, reload the page to show the updated URLs card
          if (html.includes('Custom Domain Active')) {
            window.location.reload()
          }
        }
      }
    } catch (error) {
      console.error('[DomainSetup] Polling error:', error)
    }
  }

  detectStatusChange(html) {
    // Compare key indicators in the response
    const currentContent = this.hasDomainSectionTarget ? this.domainSectionTarget.innerHTML : ''

    // Check for status indicators that match the actual partial content
    const indicators = [
      'Action Required', // dns_instructions (pending)
      'Verifying DNS Configuration', // verification_progress (verifying)
      'In Progress', // verification_progress
      'DNS Verified!', // ssl_pending (verified)
      'Provisioning SSL Certificate', // ssl_pending
      'Custom Domain Active', // connected (ready)
      'Domain Setup Failed' // failed
    ]

    for (const indicator of indicators) {
      const wasPresent = currentContent.includes(indicator)
      const isPresent = html.includes(indicator)
      if (wasPresent !== isPresent) {
        return true
      }
    }

    // Check verification attempts count changed
    const currentAttempts = currentContent.match(/Check (\d+) of/)
    const newAttempts = html.match(/Check (\d+) of/)
    if (currentAttempts && newAttempts && currentAttempts[1] !== newAttempts[1]) {
      return true
    }

    return false
  }

  // =====================
  // Modal Management
  // =====================

  openWizard(event) {
    if (event) event.preventDefault()

    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      document.body.classList.add('overflow-hidden')
      document.addEventListener('keydown', this.boundKeydown)

      // Reset wizard to step 1
      this.goToStep(1)

      // Focus domain input
      setTimeout(() => {
        if (this.hasDomainInputTarget) {
          this.domainInputTarget.focus()
        }
      }, 100)
    }
  }

  closeWizard(event) {
    if (event) event.preventDefault()

    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      document.body.classList.remove('overflow-hidden')
      document.removeEventListener('keydown', this.boundKeydown)
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeWizard()
    }
  }

  // =====================
  // Step Navigation
  // =====================

  nextStep(event) {
    if (event) event.preventDefault()

    if (!this.validateCurrentStep()) {
      return
    }

    if (this.currentStep < this.totalSteps) {
      this.goToStep(this.currentStep + 1)
    }
  }

  previousStep(event) {
    if (event) event.preventDefault()

    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1)
    }
  }

  goToStep(stepNumber) {
    this.currentStep = stepNumber

    // Update step visibility
    this.stepTargets.forEach((step) => {
      const stepNum = parseInt(step.dataset.step, 10)
      step.classList.toggle('hidden', stepNum !== stepNumber)
    })

    // Update step indicators
    this.stepIndicatorTargets.forEach((indicator) => {
      const stepNum = parseInt(indicator.dataset.step, 10)
      const isActive = stepNum === stepNumber
      const isCompleted = stepNum < stepNumber

      if (isActive) {
        indicator.style.backgroundColor = 'var(--color-gray-900)'
        indicator.style.color = 'white'
      } else if (isCompleted) {
        indicator.style.backgroundColor = 'var(--color-primary-500)'
        indicator.style.color = 'white'
      } else {
        indicator.style.backgroundColor = 'var(--bg-muted)'
        indicator.style.color = 'var(--text-tertiary)'
      }
    })

    // Update navigation buttons
    this.updateNavigationButtons()

    // Update confirmation step content
    if (stepNumber === 3) {
      this.updateConfirmation()
    }
  }

  updateNavigationButtons() {
    const isFirstStep = this.currentStep === 1
    const isLastStep = this.currentStep === this.totalSteps

    // Back button: hidden on first step, visible otherwise
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.style.display = isFirstStep ? 'none' : ''
    }

    // Next/Continue button: visible except on last step
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.display = isLastStep ? 'none' : ''
    }

    // Submit button: only visible on last step
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.style.display = isLastStep ? '' : 'none'
    }
  }

  // Prevent Enter key from submitting form prematurely
  // Instead, advance to next step if validation passes
  handleEnterKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      // If not on last step, go to next step
      if (this.currentStep < this.totalSteps) {
        this.nextStep(event)
      }
    }
  }

  // Prevent form submission unless on last step
  preventPrematureSubmit(event) {
    if (this.currentStep !== this.totalSteps) {
      event.preventDefault()
      return false
    }
  }

  // Submit form when clicking the submit button
  submitForm(event) {
    event.preventDefault()

    if (this.hasFormTarget) {
      this.formTarget.submit()
    }
  }

  // =====================
  // Form Validation
  // =====================

  validateCurrentStep() {
    switch (this.currentStep) {
      case 1:
        return this.validateDomain()
      case 2:
        return this.validateMethodSelection()
      default:
        return true
    }
  }

  validateDomain() {
    if (!this.hasDomainInputTarget) return true

    const domain = this.domainInputTarget.value.trim().toLowerCase()

    // Basic domain validation regex
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/

    if (!domain) {
      this.showDomainError('Please enter a domain name')
      return false
    }

    // Remove any http:// or https:// prefix
    const cleanDomain = domain.replace(/^https?:\/\//, '')
    this.domainInputTarget.value = cleanDomain

    if (!domainRegex.test(cleanDomain)) {
      this.showDomainError('Please enter a valid domain name (e.g., www.example.com)')
      return false
    }

    this.hideDomainError()
    return true
  }

  showDomainError(message) {
    if (this.hasDomainFeedbackTarget) {
      this.domainFeedbackTarget.classList.remove('hidden')
      this.domainFeedbackTarget.innerHTML = `
        <div class="p-3 rounded-lg" style="background-color: var(--color-error-50); border: 1px solid var(--color-error-200);">
          <p class="text-sm" style="color: var(--color-error-700);">${message}</p>
        </div>
      `
    }
    if (this.hasDomainInputTarget) {
      this.domainInputTarget.classList.add('form-input-error')
    }
  }

  hideDomainError() {
    if (this.hasDomainFeedbackTarget) {
      this.domainFeedbackTarget.classList.add('hidden')
      this.domainFeedbackTarget.innerHTML = ''
    }
    if (this.hasDomainInputTarget) {
      this.domainInputTarget.classList.remove('form-input-error')
    }
  }

  validateMethodSelection() {
    const selectedMethod = this.getSelectedMethod()
    return selectedMethod !== null
  }

  // =====================
  // DNS Method Selection
  // =====================

  selectMethod(event) {
    const option = event.currentTarget
    const method = option.dataset.method

    // Update radio button
    this.methodRadioTargets.forEach((radio) => {
      radio.checked = radio.value === method
    })

    // Update visual styling
    this.methodOptionTargets.forEach((opt) => {
      if (opt.dataset.method === method) {
        opt.style.borderColor = 'var(--color-primary-500)'
        opt.style.backgroundColor = 'var(--color-primary-50)'
      } else {
        opt.style.borderColor = 'var(--border-default)'
        opt.style.backgroundColor = 'transparent'
      }
    })
  }

  getSelectedMethod() {
    const checkedRadio = this.methodRadioTargets.find((radio) => radio.checked)
    return checkedRadio ? checkedRadio.value : null
  }

  // =====================
  // Confirmation Update
  // =====================

  updateConfirmation() {
    const domain = this.hasDomainInputTarget ? this.domainInputTarget.value.trim() : '-'
    const method = this.getSelectedMethod()
    const methodLabel = method === 'cname' ? 'CNAME Record' : 'Nameservers'

    if (this.hasConfirmDomainTarget) {
      this.confirmDomainTarget.textContent = domain
    }

    if (this.hasConfirmMethodTarget) {
      this.confirmMethodTarget.textContent = methodLabel
    }
  }

  // =====================
  // Copy to Clipboard
  // =====================

  copyToClipboard(event) {
    const button = event.currentTarget
    const textToCopy = button.dataset.copyValue

    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        // Show success feedback
        const originalText = button.innerHTML
        button.innerHTML = `
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        `
        button.style.color = 'var(--color-success-600)'

        setTimeout(() => {
          button.innerHTML = originalText
          button.style.color = ''
        }, 2000)
      }).catch((err) => {
        console.error('Failed to copy:', err)
      })
    }
  }
}
